import React from "react";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { connect } from "react-redux";
import defaultTheme, { useTheme } from "theme";
import i18n from "i18next";
import * as Sentry from "@sentry/browser";
import {
  routes,
  queryParams,
  queryParamValues,
  baseRoutes,
  hashParams,
  modalValues,
  getNonDashboardRoutes
} from "routes";
import { BrowserRouter as Router, Routes as Switch, Navigate as Redirect, Route } from "react-router-dom";
import {
  configureAmplify,
  isMobile,
  isUserSignedIn,
  signOut,
  getQueryParams,
  fetchPortfolios,
  isAppInWhiteLabelMode,
  setSiteConfig,
  fetchUser,
  userSelector,
  isAmcUser,
  signWithGoogleFlowSelector,
  isUserValid,
  isAppInViewMode,
  isAppInShareMode,
  siteConfigSelector,
  apiErrorCodes,
  appMaintenanceModeSelector,
  redirectPostSignInSelector,
  setSignInRedirectPathAction,
  shareCapabilities,
  signUpWithGoogleFlowSelector,
  multiuserInvitationDataSelector,
  getCurrentUser,
  setPortfolioSessionUserId,
  getPortfolioSessionUserId,
  injectReducersForSharedPortfolios,
  sharedPortfolioUsersSelector,
  removePortfolioSessionUserId,
  getLastUsedPortfolioUserId,
  portfoliosStateSelector,
  loadRecapDataFromLocalForage,
  loadPortfoliosDataFromLocalForage,
  tickersRehydratedPromise
} from "@kubera/common";
import InitialLoader from "components/loader/InitialLoader";
import { loadDashboardRouteForCurrentPath } from "components/dashboard/dashboardRouteChunks";
import Cookies from "js-cookie";
import IdleTimer from "react-idle-timer";
import RedirectCallbackComponent from "components/root/RedirectCallbackComponent";
import StripeCallbackComponent from "components/root/StripeCallbackComponent";
import RedirectHopComponent from "components/root/RedirectHopComponent";
import ConfirmationDialog from "components/dialog/ConfirmationDialog";
import HandleHistory from "components/history";
import MaintenanceModeComponent from "components/dashboard/MaintenanceModeComponent";
import OutageBannerComponent from "components/Banner/OutageBannerComponent";
import { ConsiderDefaultThemeContext } from "utilities/Contexts";
import LinkAccountProviderPageComponent from "components/link_account/LinkAccountProviderPageComponent";
import { isMobileHandoffReturn } from "components/link_account/LinkAccountCallbackParser";
import { LINK_CUSTODIAN_ID_KEY } from "components/link_account/LinkAccountProviderPageComponentExports";
import { SignOut, SignInCallback, ViewRoute } from "routes";
import ScrollToTop from "./ScrollToTop";
import RedirectWithLoader from "./RedirectWithLoader";
import Logo from "components/labels/AppLogo";
import DeferredPromise from "utilities/DeferredPromise";

let dashboardPreload = null;
const preloadDashboard = () => {
  if (!dashboardPreload) {
    dashboardPreload = Promise.all([
      import("components/dashboard/DashboardComponent"),
      loadDashboardRouteForCurrentPath()
    ]).then(([dashboardModule]) => dashboardModule);
  }
  return dashboardPreload;
};
const DashboardComponent = React.lazy(preloadDashboard);
const SignInComponent = React.lazy(() => import("components/signin/SignInComponent"));
const MobileSignInComponent = React.lazy(() => import("components/signin/MobileSignInComponent"));
const SignUpComponent = React.lazy(() => import("components/signin/SignUpComponent"));
const MobileSignUpComponent = React.lazy(() => import("components/signin/MobileSignUpComponent"));
const OAuthSignInComponent = React.lazy(() => import("components/signin/OAuthSignInComponent"));
const ForgotPasswordComponent = React.lazy(() => import("components/signin/ForgotPasswordComponent"));
const MobileForgotPasswordComponent = React.lazy(() => import("components/signin/MobileForgotPasswordComponent"));
const ForgotPasswordConfirmationComponent = React.lazy(() =>
  import("components/signin/ForgotPasswordConfirmationComponent")
);
const MobileForgotPasswordConfirmationComponent = React.lazy(() =>
  import("components/signin/MobileForgotPasswordConfirmationComponent")
);
const DeleteAccountConfirmationComponent = React.lazy(() =>
  import("components/account_settings/DeleteAccountConfirmationComponent")
);
const MarkUserActiveComponent = React.lazy(() => import("components/account_settings/MarkUserActiveComponent"));
const WhiteLabelDashboardComponent = React.lazy(() => import("components/white_label/WhiteLabelDashboardComponent"));
const PasscodeComponent = React.lazy(() => import("components/root/PasscodeComponent"));
const WLKAdmin = React.lazy(() => import("components/headsup/WLKAdmin"));
const RecapComponentExpanded = React.lazy(() => import("components/recap/RecapComponentExpanded"));
const CsvDownloadComponent = React.lazy(() => import("components/recap/CsvDownloadComponent"));
const FamilyInvitationComponent = React.lazy(() => import("components/multiuser/FamilyInvitationComponent"));
const MobileLinkHandoffComponent = React.lazy(() => import("components/link_account/MobileLinkHandoffComponent"));
const ExtensionPanelComponent = React.lazy(() => import("components/ai_sync_extension/ExtensionPanelComponent"));
const WlkAiSyncComponent = React.lazy(() => import("components/ai_sync_extension/WlkAiSyncComponent"));
const NetWorthStatementComponent = React.lazy(() =>
  import("components/net_worth/NetWorthStatementComponent").then(module => ({
    default: module.NetWorthStatementComponent
  }))
);
const ProofOfWealthComponent = React.lazy(() =>
  import("components/proof/index").then(module => ({ default: module.FinancialStatement }))
);

const isMobileDevice = isMobile();

const MobileBodyBackground = createGlobalStyle`
  body {
    background: ${props => props.theme.mobileBackground};
  }
`;

const Container = styled.div`
  display: block;
  height: 100%;
  background: ${props => (isMobileDevice ? props.theme.mobileBackground : props.theme.initialLoaderBG)};

  polyline {
    stroke: ${props => props.theme.polylineStroke};
  }
`;

const OutageBanner = styled(OutageBannerComponent)``;

const MobileConfirmationDialog = styled(ConfirmationDialog)`
  width: 100%;
  white-space: pre-wrap;
  box-sizing: border-box;
  padding: 2rem 1.5625rem 2.6875rem;

  * {
    box-sizing: border-box;
  }
`;

const AppLogo = styled(Logo)`
  margin-bottom: 2rem;
`;

const InviteMessageContainer = styled(Container)`
  margin-top; 1.875rem;
  padding: 0 1.875rem;
  background: ${props => props.theme.mobileSignInBackground};
`;

const InviteMessageBlock = styled.div`
  padding-top: 2rem;
`;

const InviteMessageTitle = styled.div`
  font-size: 1.625rem;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.024375rem;
  margin-bottom: 1.6875rem;
`;

const InviteMessageDesc = styled.div`
  font-size: 1.125rem;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: -0.016875rem;
`;

const trackOpenPage = function() {
  localStorage.setItem("openpages", Date.now());
  return new Promise(resolve => {
    const onLocalStorageEvent = function(e) {
      if (e.key === "openpages") {
        // Emit that you're already available.
        localStorage.setItem("page_available", Date.now());
      }
      if (e.key === "page_available") {
        resolve(true);
      }
    };

    const rememberMe = localStorage.getItem("remember_me");

    if (rememberMe !== "0") {
      resolve(false);
    }
    setTimeout(() => {
      resolve(false);
    }, 1000);
    window.addEventListener("storage", onLocalStorageEvent, false);
  });
};

const isInViewMode = isAppInShareMode();

// const mobileSupportedEnvs = ["local", "staging"];

const ThemeWrapper = ({ children }) => {
  const theme = useTheme();

  const proofBaseRoute = routes.PROOF_OF_WEALTH.split("/:")[0];
  const activeTheme = window.location.pathname.includes(proofBaseRoute) ? defaultTheme : theme;

  return (
    <ThemeProvider theme={activeTheme}>
      {isMobileDevice && <MobileBodyBackground />}
      {children}
    </ThemeProvider>
  );
};

// To resolve after setSiteConfig and Amplify.configure is called
const isAmplifyConfiguredPromise = new DeferredPromise();
class RootComponent extends React.Component {
  constructor(props) {
    super(props);

    this.idleTimer = null;
    this.handleOnIdle = this.handleOnIdle.bind(this);
    this.handleSignInCallback = this.handleSignInCallback.bind(this);
    this.handleOnSiteConfigLoadRefresh = this.handleOnSiteConfigLoadRefresh.bind(this);
    this.refreshUser = this.refreshUser.bind(this);
    this.handleRememberMe = this.handleRememberMe.bind(this);
    this.handleGoogleSignInDialogPositiveButtonClick = this.handleGoogleSignInDialogPositiveButtonClick.bind(this);
    this.handleGoogleSignInDialogNegativeButtonClick = this.handleGoogleSignInDialogNegativeButtonClick.bind(this);
    this.handleMultiuserSignUpEmailMismatchDialogButtonClick = this.handleMultiuserSignUpEmailMismatchDialogButtonClick.bind(
      this
    );
    this.redirectToHome = this.redirectToHome.bind(this);

    this.shouldTriggerRefreshOnTabFocus = false;

    const user = this.getUser();
    // If there is no user but a session user id is there remove it as
    // without the user object we can't know if its for current user or not
    // and hence can't make right API calls. E.g. when the indexed data is
    // not available for some reason or has been manually deleted
    if (!user === true && !getPortfolioSessionUserId() === false) {
      console.log("Remove old session *************");
      removePortfolioSessionUserId();
    }
    const sharedPortfolioUsers = this.props.sharedPortfolioUsers;
    if (sharedPortfolioUsers.length > 0) {
      injectReducersForSharedPortfolios(sharedPortfolioUsers);
    }
    const sessionPortfolioUserId = getPortfolioSessionUserId();
    if (!window.portfolioUserId === false) {
      setPortfolioSessionUserId(window.portfolioUserId);
    } else if (!sessionPortfolioUserId === true && !getLastUsedPortfolioUserId() === false) {
      setPortfolioSessionUserId(getLastUsedPortfolioUserId());
    } else if (!sessionPortfolioUserId === true && !user === false) {
      setPortfolioSessionUserId(user.id);
    }

    console.log("Session being used:" + getPortfolioSessionUserId());

    this.state = {
      isSiteConfigFetchPending: true,
      siteConfigLoadError: null,
      isAuthCheckPending: true,
      isSignedIn: false,
      isUserFetchPending: false,
      userFetchError: null,
      isTrackingPageOpen: true,
      showGoogleSignInConfirmationDialog: false,
      linkExpiredError: null,
      siteConfigLoadErrorCode: null,
      userFetchErrorCode: null,
      showMultiuserSignupEmailMismatchDialog: false
    };

    this.fetchSiteConfig();
  }

  async componentDidMount() {
    this.handleSignOutIfNeeded();
    window.addEventListener("pageshow", this.handlePageShow);

    const recapRehydratedPromise = this.props.store.dispatch(loadRecapDataFromLocalForage());
    const portfoliosRehydratedPromise = this.props.store.dispatch(loadPortfoliosDataFromLocalForage());
    if ([routes.HOME, routes.NET_WORTH, routes.REPORTS, routes.REPORTS_EXPANDED].includes(window.location.pathname)) {
      await recapRehydratedPromise;
    }
    await portfoliosRehydratedPromise;
    await tickersRehydratedPromise;
    if (
      !this.props.portfoliosState?.portfolios &&
      !isInViewMode &&
      ([routes.LINK_PROVIDER_PAGE, routes.REPORTS_EXPANDED, routes.NETWORTH_STATEMENT, routes.DOWNLOAD_FILE].includes(
        window.location.pathname
      ) ||
        window.location.pathname.startsWith(baseRoutes.PROOF_OF_WEALTH))
    ) {
      // Wait for Amplify.configure
      await isAmplifyConfiguredPromise;
      const isSignedIn = await isUserSignedIn();
      if (isSignedIn) {
        // For routes not going to dashboard component where fetchPortfolios won't get called
        console.log("Local portfolios array empty: fetching");
        await this.props.store.dispatch(fetchPortfolios());
        console.log("Local portfolios array empty: after fetch state", this.props.portfoliosState?.portfolios);
      }
    }
    const isSignedIn = await this.handleRememberMe();
    this.setState({
      isTrackingPageOpen: !isSignedIn
    });

    if (isSignedIn) {
      this.refreshUser(false);
    }

    window.addEventListener("beforeunload", this.captureMemoryUsageOnUnload);
  }

  handleSignOutIfNeeded = () => {
    if (window.isSigningOut) {
      signOut();
    }
  };

  handlePageShow = event => {
    if (event.persisted) {
      this.handleSignOutIfNeeded();
    }
  };

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.captureMemoryUsageOnUnload);
    window.removeEventListener("pageshow", this.handlePageShow);
  }

  captureMemoryUsageOnUnload = () => {
    // @TODO: Comment until new recap optimisation goes live
    // captureHighMemoryUsageInSentry(store.getState(), "EXIT");
  };

  async handleRememberMe() {
    if (getNonDashboardRoutes().includes(window.location.pathname)) {
      return true;
    }

    const rememberMe = localStorage.getItem("remember_me");
    const isActiveSession = sessionStorage.getItem("is_active_session");
    sessionStorage.setItem("is_active_session", "1");

    const isPageAlreadyOpen = await trackOpenPage();
    if (rememberMe === "0" && !isPageAlreadyOpen && !isActiveSession) {
      Sentry.withScope(function(scope) {
        scope.setTag("signout_exception", "Uncaught Exception");
        Sentry.captureException("RootComponent handleRememberMe signOut triggered.");
      });
      signOut();
      return false;
    }
    return true;
  }

  refreshUser(callApi) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    if (callApi) {
      this.props.store.dispatch(fetchUser());
    }

    setTimeout(() => {
      this.refreshUser(true);
    }, tomorrow.getTime() - today.getTime());
  }

  fetchSiteConfig(isRetry = false) {
    if (isRetry === true) {
      this.setState({ isSiteConfigFetchPending: true, siteConfigLoadError: null });
    }

    this.props.store.dispatch(
      setSiteConfig(
        siteConfig => {
          if (!siteConfig.tsExpiry === false && siteConfig.tsExpiry - Math.floor(new Date().getTime() / 1000) < 0) {
            this.setState({ linkExpiredError: i18n.t("linkExpired") });
            return;
          }

          this.setState({ isSiteConfigFetchPending: false });

          document.title = isAppInWhiteLabelMode()
            ? i18n.t("wlPageTitle").replace("%s1%", siteConfig.shortName)
            : i18n.t("appName");
          localStorage.setItem("wl_page_title", document.title);

          this.handleSiteConfigOnLoad();
        },
        apiError => {
          isAmplifyConfiguredPromise.resolve();
          this.setState({
            siteConfigLoadError: apiError.errorMessage,
            siteConfigLoadErrorCode: apiError.errorCode,
            ...(apiError.errorCode === apiErrorCodes.INVALID_INPUT && { linkExpiredError: i18n.t("linkExpired") })
          });
        }
      )
    );
  }

  handleSiteConfigOnLoad() {
    configureAmplify(this.props.store.getState());
    isAmplifyConfiguredPromise.resolve();

    this.checkForUserSignInStatus(true);
    this.setSourceCookieIfNeeded();

    document.addEventListener("visibilitychange", () => {
      if (
        this.state.isSignedIn === true &&
        document.hidden === false &&
        (this.shouldTriggerRefreshOnTabFocus === true || isMobileDevice === true)
      ) {
        this.shouldTriggerRefreshOnTabFocus = false;
        this.props.store.dispatch(fetchPortfolios(isMobileDevice === false));
      }
    });
  }

  handleOnSiteConfigLoadRefresh() {
    if (this.state.userFetchErrorCode === apiErrorCodes.CLIENT_BREAKING_CHANGES) {
      window.location.reload();
      return;
    }
    this.fetchSiteConfig(true);
  }

  handleOnIdle(event) {
    if (this.state.isSignedIn === false) {
      return;
    }

    const rememberMe = localStorage.getItem("remember_me");

    if (rememberMe === "0") {
      Sentry.withScope(scope => {
        scope.setTag("signout_exception", "Uncaught Exception");
        Sentry.captureException("RootComponent handleOnIdle signOut triggered. User id:" + this.getUserId());
      });
      signOut();
    }

    if (document.hidden === false) {
      this.props.store.dispatch(fetchPortfolios(true));
    } else {
      this.shouldTriggerRefreshOnTabFocus = true;
    }
  }

  setSourceCookieIfNeeded() {
    const pathName = window.location.pathname;
    if (pathName === "/" || pathName === routes.SIGNUP) {
      if (Cookies.get("k-utm")) {
        return;
      }
      const queryParams = getQueryParams(window.location);
      var trackerDetails = {};

      if (!queryParams["utm_source"] === false) {
        trackerDetails["source"] = queryParams["utm_source"];
      }
      if (!queryParams["utm_medium"] === false) {
        trackerDetails["medium"] = queryParams["utm_medium"];
      }
      if (!queryParams["utm_campaign"] === false) {
        trackerDetails["campaign"] = queryParams["utm_campaign"];
      }
      if (!queryParams["utm_term"] === false) {
        trackerDetails["term"] = queryParams["utm_term"];
      }
      if (!queryParams["utm_content"] === false) {
        trackerDetails["content"] = queryParams["utm_content"];
      }
      if (!document.referrer === false) {
        trackerDetails["referrer"] = document.referrer;
      }

      if (Object.keys(trackerDetails).length > 0) {
        Cookies.set("k-utm", JSON.stringify(trackerDetails), {
          expires: 1,
          path: "",
          domain: ".kubera.com",
          sameSite: "Strict",
          secure: true
        });
      }
    }
  }

  checkForUserSignInStatus = async (shouldRefreshUserDetails = false) => {
    if (isInViewMode === true) {
      this.setState({ isAuthCheckPending: false, isSignedIn: true });
      return;
    }

    const portfoliosState = portfoliosStateSelector(this.props.store.getState());
    if (!window.location.pathname.startsWith(baseRoutes.PROOF_OF_WEALTH) && !portfoliosState === true) {
      console.log(
        "Mismatch between session id and user data, need to logout ************* session id:" +
          getPortfolioSessionUserId()
      );
      this.setState({ isAuthCheckPending: false, isSignedIn: false });
      signOut();
      return;
    }

    const status = await isUserSignedIn();
    if (status === true) {
      // 86400000 (oneDayInMillis) * 45 (days)
      let maxAllowedMillis = 3888000000;
      const timeNow = Date.now();
      const lastLogin = Cookies.get("kuberaAppLastAccessSecure");
      if (!lastLogin === false && timeNow - lastLogin > maxAllowedMillis) {
        signOut(true);
        return;
      }

      Cookies.set("kuberaAppLastAccessSecure", timeNow, {
        sameSite: "Strict",
        secure: true,
        expires: 365,
        domain: ".kubera.com"
      });
      Cookies.set("kuberaAppDomain", window.location.host, {
        path: "/",
        domain: ".kubera.com",
        expires: 365,
        sameSite: "Strict",
        secure: true
      });
      sessionStorage.removeItem("kuberaRecaptchaVerifyResponse");

      // Don't call fetch user if the user is signing in from the sign in callback
      // We need to give priority to the logic inside the sign in callback
      // so that things like user isValid call can happen before get user etc
      // for Google sign in flows
      if (
        shouldRefreshUserDetails === true &&
        window.location.pathname !== routes.SIGNOUT &&
        window.location.pathname !== routes.SIGNIN_CALLBACK
      ) {
        this.props.store.dispatch(
          fetchUser(
            userData => {
              if (!userData.wl === false && !userData.wl.tsArchive === false) {
                this.signOutArchivedUser(userData);
              } else {
                this.setState({ isAuthCheckPending: false, isUserFetchPending: false, isSignedIn: status });
              }
            },
            apiError => {
              this.setState({
                isUserFetchPending: true,
                userFetchError: apiError.errorMessage,
                userFetchErrorCode: apiError.errorCode
              });

              Sentry.withScope(scope => {
                scope.setTag("initial_user_fetch_exception", "Initial User Fetch Exception");
                Sentry.captureException("Initial user fetch error: " + apiError.errorMessage);
              });
            }
          )
        );
      }
    }

    const invitationData = this.props.multiuserInvitationData;
    const params = getQueryParams(window.location);
    if (!params[queryParams.INVITATION_ID] === false) {
      invitationData.email = params[queryParams.EMAIL_ID];
      invitationData.id = params[queryParams.INVITATION_ID];
    }
    if (status === true && !invitationData.email === false) {
      getCurrentUser(
        user => {
          if (user.signInUserSession?.idToken?.payload?.email === invitationData.email) {
            this.setState({ isAuthCheckPending: false, isSignedIn: status });
          } else {
            this.setState({ showMultiuserSignupEmailMismatchDialog: true, isUserFetchPending: false });
          }
        },
        error => {
          this.setState({ userFetchError: error.message });
        }
      );
    } else {
      this.setState({ isAuthCheckPending: false, isSignedIn: status });
    }
  };

  signOutArchivedUser(userData) {
    signOut(
      true,
      `${queryParams.SIGNIN_ERROR}=${queryParamValues.ARCHIVED_USER_ERROR}&${queryParams.EMAIL_ID}=${userData.wl.email}`
    );
    Sentry.withScope(scope => {
      scope.setTag("signout_exception", "Uncaught Exception");
      Sentry.captureException("RootComponent signOutArchivedUser signOut triggered. User id:" + this.getUserId());
    });
  }

  getUser() {
    return this.props.user;
  }

  getUserId() {
    const user = this.getUser();

    if (!user === true) {
      return "<null>";
    }
    return user.id;
  }

  handleSignInCallback() {
    this.checkForUserSignInStatus();

    if (!this.getUser() === false) {
      if (this.state.userFetchErrorCode === apiErrorCodes.CLIENT_BREAKING_CHANGES) {
        window.location.reload();
        return;
      }
      return;
    }
    removePortfolioSessionUserId();
    this.setState({ isUserFetchPending: true, userFetchError: null });

    const getUserData = () => {
      this.props.store.dispatch(
        fetchUser(
          userData => {
            if (!userData.wl === false && !userData.wl.tsArchive === false) {
              this.signOutArchivedUser(userData);
            } else {
              this.setState({ isUserFetchPending: false });
              const portfolioSessionId = getPortfolioSessionUserId();
              if (!portfolioSessionId === true) {
                setPortfolioSessionUserId(userData.id);
              }
            }
          },
          apiError => {
            this.setState({ userFetchError: apiError.errorMessage, userFetchErrorCode: apiError.errorCode });
          }
        )
      );
    };

    const invitationData = this.props.multiuserInvitationData;
    if (!invitationData.id === false && !this.props.signUpWithGoogleFlow === false) {
      if (this.state.isSignedIn === true) {
        getCurrentUser(
          user => {
            if (user.signInUserSession?.idToken?.payload?.email === invitationData.email) {
              const urlParams = new URLSearchParams(window.location.search);
              urlParams.set(queryParams.INVITATION_ID, invitationData.id);
              if (invitationData.portfolioId) {
                urlParams.set(queryParams.PORTFOLIO_ID, invitationData.portfolioId);
              }
              window.location = `${routes.FAMILY_INVITATION}?${urlParams.toString()}`;
              getUserData();
            } else {
              this.setState({ showMultiuserSignupEmailMismatchDialog: true, isUserFetchPending: false });
            }
          },
          error => {
            this.setState({ userFetchError: error.message });
          }
        );
      } else {
        setTimeout(() => {
          this.handleSignInCallback();
        }, 1000);
      }
    } else if (this.state.showGoogleSignInConfirmationDialog === false && !this.props.signWithGoogleFlow === false) {
      this.props.store.dispatch(
        isUserValid(
          isValid => {
            if (isValid === true) {
              getUserData();
            } else {
              this.setState({ showGoogleSignInConfirmationDialog: true, isUserFetchPending: false });
            }
          },
          apiError => {
            this.setState({ userFetchError: apiError.errorMessage });
          }
        )
      );
    } else {
      getUserData();
    }
  }

  handleInitialLoaderRefresh = () => {
    window.location.reload();
  };

  handleSignOutCallback() {
    setTimeout(() => {
      this.setState({ ...this.state, isSignedIn: false });
    }, 300);
  }

  renderHomeRedirect = params => {
    if (this.state.isSignedIn === true) {
      preloadDashboard();
    }
    if (isMobileDevice) {
      return <Redirect to={routes.MOBILENAV} />;
    }
    return (
      <RedirectWithLoader
        to={{
          pathname: routes.NET_WORTH,
          search: params[queryParams.PORTFOLIO_ID]
            ? `?${queryParams.PORTFOLIO_ID}=${params[queryParams.PORTFOLIO_ID]}`
            : ""
        }}
      />
    );
  };

  redirectToHome() {
    const redirectPostSignInPath = this.props.redirectPostSignInPath;
    if (redirectPostSignInPath && redirectPostSignInPath.pathname === routes.AI_SYNC_EXTENSION_PANEL) {
      this.props.store.dispatch(setSignInRedirectPathAction(null));
      if (window.opener) {
        window.opener.postMessage({ type: "K-EXT-LOGIN-COMPLETE" }, window.location.origin);
      }
      window.close();
      return null;
    }

    if (this.props.isAmcUserFlag === false) {
      const params = getQueryParams(window.location);
      if (!params[queryParams.INVITATION_ID] === false) {
        return (
          <Redirect
            to={{
              pathname: routes.FAMILY_INVITATION,
              search: window.location.search,
              hash: window.location.hash
            }}
          />
        );
      }

      const redirectPostSignInPath = this.props.redirectPostSignInPath;
      if (
        !redirectPostSignInPath === false &&
        this.getDashboardRoutes().includes(redirectPostSignInPath.pathname) === false &&
        redirectPostSignInPath.pathname === routes.FAMILY_INVITATION
      ) {
        setTimeout(() => {
          this.props.store.dispatch(setSignInRedirectPathAction(null));
        }, 2000);
        return (
          <Redirect
            to={{
              pathname: routes.FAMILY_INVITATION,
              search: redirectPostSignInPath.search,
              hash: redirectPostSignInPath.hash
            }}
          />
        );
      } else if (redirectPostSignInPath !== null && redirectPostSignInPath.pathname === routes.OAUTH_SIGNIN) {
        setTimeout(() => {
          this.props.store.dispatch(setSignInRedirectPathAction(null));
        }, 2000);
        return (
          <Redirect
            to={{
              pathname: routes.OAUTH_SIGNIN,
              search: redirectPostSignInPath.search,
              hash: redirectPostSignInPath.hash
            }}
          />
        );
      }
    }
    return <Redirect to={routes.HOME} />;
  }

  getDashboardRoutes() {
    var dashboardRoutes = [
      routes.ASSETS,
      routes.DEBTS,
      routes.NET_WORTH,
      routes.REPORTS,
      routes.MOBILENAV,
      routes.WHATS_NEW_POPUP,
      routes.REPORTS_EXPANDED,
      routes.PLANNING,
      routes.PLUGIN
    ];

    if (isMobileDevice) {
      dashboardRoutes.push(routes.INVESTABLE);
    }

    if (isAppInViewMode()) {
      const siteConfig = this.props.siteConfig;
      const capabilities = JSON.parse(siteConfig.capability);
      var routesToRemove = [];

      if (
        capabilities.includes(shareCapabilities.ALL) === false &&
        capabilities.includes(shareCapabilities.NETWORTH) === false
      ) {
        routesToRemove.push(routes.NET_WORTH);
      }
      if (
        capabilities.includes(shareCapabilities.ALL) === false &&
        capabilities.includes(shareCapabilities.RECAP) === false
      ) {
        routesToRemove.push(routes.REPORTS);
      }
      if (
        capabilities.includes(shareCapabilities.ALL) === false &&
        capabilities.includes(shareCapabilities.PLANNING) === false
      ) {
        routesToRemove.push(routes.PLANNING);
      }
      dashboardRoutes = dashboardRoutes.filter(item => routesToRemove.includes(item) === false);
    }
    return dashboardRoutes;
  }

  handleGoogleSignInDialogPositiveButtonClick(e) {
    this.setState({ showGoogleSignInConfirmationDialog: false, isUserFetchPending: true });

    this.props.store.dispatch(
      fetchUser(
        userData => {
          this.setState({ isUserFetchPending: false });
        },
        apiError => {
          this.setState({ userFetchError: apiError.errorMessage });
        }
      )
    );
  }

  handleGoogleSignInDialogNegativeButtonClick(e) {
    signOut();
  }

  handleMultiuserSignUpEmailMismatchDialogButtonClick(e) {
    signOut();
  }

  signoutCallback = () => {
    this.setState({
      isSignedIn: false
    });
  };

  render() {
    window.renderlog("Render: RootComponent");

    const params = getQueryParams(window.location);
    const custodianId =
      params[queryParams.ID] ||
      sessionStorage.getItem(LINK_CUSTODIAN_ID_KEY) ||
      localStorage.getItem(LINK_CUSTODIAN_ID_KEY);

    if (this.props.appMaintenanceMode) {
      return (
        <ConsiderDefaultThemeContext.Provider value={true}>
          <ThemeWrapper>
            <MaintenanceModeComponent />
          </ThemeWrapper>
        </ConsiderDefaultThemeContext.Provider>
      );
    }

    if (this.state.showGoogleSignInConfirmationDialog === true) {
      if (isMobileDevice) {
        return (
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <MobileConfirmationDialog
              title={i18n.t("mobileSignIn.newUserDialogTitle")}
              description={i18n.t("mobileSignIn.newUserDialogDesc")}
              positiveButtonTitle={i18n.t("ok")}
              handlePositiveButtonClick={this.handleGoogleSignInDialogNegativeButtonClick}
            />
          </Router>
        );
      }

      return (
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <ConfirmationDialog
            title={i18n.t("signInWithGoogleDialog.title")}
            description={i18n.t("signInWithGoogleDialog.description")}
            positiveButtonTitle={i18n.t("signUp")}
            negativeButtonTitle={i18n.t("cancel")}
            handlePositiveButtonClick={this.handleGoogleSignInDialogPositiveButtonClick}
            handleNegativeButtonClick={this.handleGoogleSignInDialogNegativeButtonClick}
          />
        </Router>
      );
    }

    if (this.state.showMultiuserSignupEmailMismatchDialog === true) {
      if (isMobileDevice) {
        return (
          <ThemeWrapper>
            <InviteMessageContainer>
              <InviteMessageBlock>
                <AppLogo hasNoLink={isAppInWhiteLabelMode()} forceDefault />
                <InviteMessageTitle>{`${i18n.t("sorry")}!`}</InviteMessageTitle>
                <InviteMessageDesc>{i18n.t("mobileFamilyInviteMessage")}</InviteMessageDesc>
              </InviteMessageBlock>
            </InviteMessageContainer>
          </ThemeWrapper>
        );
      }

      return (
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <ConfirmationDialog
            title={i18n.t("multiuserSignupEmailMismatch.title")}
            description={i18n.t("multiuserSignupEmailMismatch.description")}
            positiveButtonTitle={i18n.t("ok")}
            handlePositiveButtonClick={this.handleMultiuserSignUpEmailMismatchDialogButtonClick}
          />
        </Router>
      );
    }

    if (!this.state.linkExpiredError === false || !this.state.siteConfigLoadError === false) {
      if (this.state.siteConfigLoadErrorCode === apiErrorCodes.WL_DOMAIN_DISABLED) {
        return (
          <ThemeWrapper>
            <InitialLoader
              errorMessage={i18n.t("disabledWlkErrorMessage")}
              overrideGenericError={true}
              disableRefresh={true}
            />
          </ThemeWrapper>
        );
      }
      if (!!this.state.linkExpiredError === false) {
        return (
          <ConsiderDefaultThemeContext.Provider value={true}>
            <ThemeWrapper>
              <MaintenanceModeComponent />
            </ThemeWrapper>
          </ConsiderDefaultThemeContext.Provider>
        );
      }
      return (
        <ThemeWrapper>
          <InitialLoader
            errorMessage={i18n.t("portfolioExpiredError")}
            overrideGenericError={true}
            disableRefresh={true}
          />
        </ThemeWrapper>
      );
    }

    if (this.state.isSiteConfigFetchPending === true || !this.state.siteConfigLoadError === false) {
      return (
        <ThemeWrapper>
          <InitialLoader errorMessage={this.state.siteConfigLoadError} onRefresh={this.handleOnSiteConfigLoadRefresh} />
        </ThemeWrapper>
      );
    }

    if (this.state.isAuthCheckPending === true || this.state.isTrackingPageOpen) {
      return (
        <ThemeWrapper>
          <InitialLoader errorMessage={this.state.userFetchError} onRefresh={this.handleOnSiteConfigLoadRefresh} />
        </ThemeWrapper>
      );
    }

    if (this.state.isUserFetchPending === true || !this.state.userFetchError === false) {
      const isUserSuspended = this.state.userFetchErrorCode === apiErrorCodes.ACCOUNT_SUSPENDED;
      return (
        <ThemeWrapper>
          <InitialLoader
            errorMessage={isUserSuspended ? i18n.t("accountSuspendedError") : this.state.userFetchError}
            onRefresh={this.handleInitialLoaderRefresh}
            overrideGenericError={isUserSuspended}
            disableRefresh={isUserSuspended}
          />
        </ThemeWrapper>
      );
    }

    if (window.isSigningOut && !getNonDashboardRoutes().includes(window.location.pathname)) {
      return (
        <ThemeWrapper>
          <InitialLoader disableRefresh={true} />
        </ThemeWrapper>
      );
    }

    return (
      <ConsiderDefaultThemeContext.Provider value={!this.state.isSignedIn}>
        <ThemeWrapper>
          <Container>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <HandleHistory />
              <IdleTimer
                ref={ref => {
                  this.idleTimer = ref;
                }}
                timeout={1000 * 60 * 60} // 60 minutes
                onIdle={this.handleOnIdle}
                debounce={250}
              />
              <ScrollToTop />
              <React.Suspense fallback={<InitialLoader />}>
                <Switch>
                  <Route
                    exact
                    path={routes.SIGNIN}
                    element={
                      this.state.isSignedIn === true ? (
                        this.redirectToHome()
                      ) : isMobileDevice ? (
                        <MobileSignInComponent />
                      ) : (
                        <SignInComponent />
                      )
                    }
                  />
                  <Route
                    exact
                    path={routes.OAUTH_SIGNIN}
                    element={<OAuthSignInComponent isSignedIn={this.state.isSignedIn} />}
                  />
                  <Route
                    exact
                    path={routes.SIGNUP}
                    element={
                      this.state.isSignedIn === true ? (
                        this.redirectToHome()
                      ) : isAppInWhiteLabelMode() === true ? (
                        <Redirect to={routes.SIGNIN} />
                      ) : isMobileDevice ? (
                        <MobileSignUpComponent />
                      ) : (
                        <SignUpComponent />
                      )
                    }
                  />
                  <Route
                    exact
                    path={routes.FORGOT_PASSWORD}
                    element={
                      this.state.isSignedIn ? (
                        this.redirectToHome()
                      ) : isMobileDevice ? (
                        <MobileForgotPasswordComponent />
                      ) : (
                        <ForgotPasswordComponent />
                      )
                    }
                  />
                  <Route
                    exact
                    path={routes.FORGOT_PASSWORD_CONFIRMATION}
                    element={
                      this.state.isSignedIn === true ? (
                        this.redirectToHome()
                      ) : isMobileDevice ? (
                        <MobileForgotPasswordConfirmationComponent />
                      ) : (
                        <ForgotPasswordConfirmationComponent />
                      )
                    }
                  />
                  <Route exact path={routes.SET_USER_ACTIVE} element={<MarkUserActiveComponent />} />
                  <Route
                    exact
                    path={routes.DELETE_ACCOUNT_CONFIRMATION}
                    element={<DeleteAccountConfirmationComponent />}
                    action={() => {
                      setTimeout(() => {
                        this.setState({ ...this.state, isSignedIn: false });
                      }, 1000);
                    }}
                  />
                  <Route
                    exact
                    path={routes.LINK_PROVIDER_PAGE}
                    element={
                      isMobileHandoffReturn() === true ? (
                        <MobileLinkHandoffComponent />
                      ) : (
                        <LinkAccountProviderPageComponent custodianId={custodianId} />
                      )
                    }
                  />
                  <Route exact path={routes.LINK_ACCOUNT_HANDOFF} element={<MobileLinkHandoffComponent />} />
                  <Route
                    exact
                    path={routes.AI_SYNC_EXTENSION_PANEL}
                    element={<ExtensionPanelComponent isSignedIn={this.state.isSignedIn} />}
                  />
                  <Route exact path={routes.WLK_AI_SYNC} element={<WlkAiSyncComponent />} />
                  <Route exact path={routes.REDIRECT_CALLBACK} element={<RedirectCallbackComponent />} />
                  <Route exact path={routes.STRIPE_CALLBACK} element={<StripeCallbackComponent />} />
                  <Route exact path={routes.REDIRECT_HOP} element={<RedirectHopComponent />} />
                  <Route
                    exact
                    path={routes.SIGNIN_CALLBACK}
                    element={
                      <SignInCallback
                        isSignedIn={this.state.isSignedIn}
                        redirectToHome={this.redirectToHome}
                        handleSignInCallback={this.handleSignInCallback}
                      />
                    }
                  />
                  <Route
                    exact
                    path={routes.SIGNOUT}
                    element={<SignOut isSignedIn={this.state.isSignedIn} signoutCallback={this.signoutCallback} />}
                  />
                  <Route
                    exact
                    path={routes.SIGNOUT_CALLBACK}
                    element={<Redirect to={routes.SIGNIN} />}
                    action={() => {
                      this.handleSignOutCallback();
                    }}
                  />
                  <Route
                    exact
                    path={routes.HOME}
                    element={
                      this.props.isAmcUserFlag === true ? (
                        isMobileDevice ? (
                          <WLKAdmin />
                        ) : (
                          <Redirect to={routes.WL_DASHBOARD} />
                        )
                      ) : (
                        this.renderHomeRedirect(params)
                      )
                    }
                  />
                  <Route exact path={baseRoutes.VIEW} element={<ViewRoute />} />
                  <Route exact path={routes.PASSCODE} element={<PasscodeComponent />} />
                  <Route
                    path={routes.REPORTS_EXPANDED}
                    element={
                      this.state.isSignedIn ? (
                        <RecapComponentExpanded isSubscriptionRequired={this.isSubscriptionRequired} />
                      ) : (
                        <Redirect to={routes.SIGNIN} />
                      )
                    }
                  />
                  <Route
                    path={routes.NETWORTH_STATEMENT}
                    element={
                      this.state.isSignedIn ? (
                        <NetWorthStatementComponent isSubscriptionRequired={this.isSubscriptionRequired} />
                      ) : (
                        <Redirect to={routes.SIGNIN} />
                      )
                    }
                  />
                  <Route
                    exact
                    path={routes.PROOF_OF_WEALTH}
                    element={this.state.isSignedIn ? <ProofOfWealthComponent /> : <Redirect to={routes.SIGNIN} />}
                  />
                  <Route exact path={baseRoutes.PROOF_OF_WEALTH} element={<ProofOfWealthComponent />} />
                  <Route
                    path={routes.DOWNLOAD_FILE}
                    element={this.state.isSignedIn ? <CsvDownloadComponent /> : <Redirect to={routes.SIGNIN} />}
                  />
                  <Route
                    path={routes.FAMILY_INVITATION}
                    element={
                      this.state.isSignedIn ? (
                        <FamilyInvitationComponent />
                      ) : (
                        <Redirect
                          to={{
                            pathname: routes.SIGNIN,
                            search: window.location.search,
                            hash: window.location.hash
                          }}
                        />
                      )
                    }
                  />
                  <Route
                    exact
                    path={routes.BENEFICIARY}
                    element={
                      <Redirect
                        to={{ pathname: routes.NET_WORTH, hash: `#${hashParams.MODAL}=${modalValues.BENEFICIARY}` }}
                      />
                    }
                  />
                  <Route
                    path="/*"
                    element={
                      this.state.isSignedIn ? (
                        <DashboardComponent isSubscriptionRequired={this.isSubscriptionRequired} />
                      ) : (
                        <Redirect
                          to={{
                            pathname: routes.SIGNIN,
                            search:
                              !window.location.search === true
                                ? ""
                                : `${window.location.search}&${queryParams.REDIRECT_PATH}=${window.location.pathname}`,
                            hash: window.location.hash
                          }}
                        />
                      )
                    }
                  />
                  <Route exact path={routes.OLD_WL_DASHBOARD} element={<Redirect to={routes.WL_DASHBOARD} />} />
                  <Route
                    path={routes.WL_DASHBOARD}
                    element={
                      this.state.isSignedIn ? (
                        <WhiteLabelDashboardComponent isSubscriptionRequired={this.isSubscriptionRequired} />
                      ) : (
                        <Redirect to={routes.SIGNIN} />
                      )
                    }
                  />
                </Switch>
              </React.Suspense>
            </Router>
            <OutageBanner />
          </Container>
        </ThemeWrapper>
      </ConsiderDefaultThemeContext.Provider>
    );
  }
}

const mapStateToProps = state => ({
  sharedPortfolioUsers: sharedPortfolioUsersSelector(state),
  portfoliosState: portfoliosStateSelector(state),
  multiuserInvitationData: multiuserInvitationDataSelector(state),
  user: userSelector(state),
  isAmcUserFlag: isAmcUser(state),
  signWithGoogleFlow: signWithGoogleFlowSelector(state),
  signUpWithGoogleFlow: signUpWithGoogleFlowSelector(state),
  redirectPostSignInPath: redirectPostSignInSelector(state),
  siteConfig: siteConfigSelector(state),
  appMaintenanceMode: appMaintenanceModeSelector(state)
});

const mapDispatchToProps = {
  setSignInRedirectPathAction
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RootComponent);
